import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import {
  CreateVisitasPlanDto,
  CreateVisitasPlanItemDto,
  UpdateVisitasPlanDto,
  VisitasPlanDto,
} from '../../../types/visitas';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import DireccionClienteSearch from '../../../components/Tesoreria/DireccionClienteSearch';

interface VendedorOption {
  nroVendedor: number;
  nombreVendedor?: string;
}

interface PlanFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  vendedores: VendedorOption[];
  plan?: VisitasPlanDto | null;
  saving?: boolean;
  onClose: () => void;
  onSubmitCreate: (payload: CreateVisitasPlanDto) => Promise<void>;
  onSubmitEdit: (payload: UpdateVisitasPlanDto) => Promise<void>;
}

const formatDateForInput = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTimeForInput = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const PlanFormDialog: React.FC<PlanFormDialogProps> = ({
  open,
  mode,
  vendedores,
  plan,
  saving = false,
  onClose,
  onSubmitCreate,
  onSubmitEdit,
}) => {
  const [fecha, setFecha] = useState('');
  const [nroVendedor, setNroVendedor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [estadoPlan, setEstadoPlan] = useState('');
  const [items, setItems] = useState<CreateVisitasPlanItemDto[]>([]);
  const [clienteNombres, setClienteNombres] = useState<string[]>([]);
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [clientePickerIndex, setClientePickerIndex] = useState<number | null>(null);
  const [direccionLabels, setDireccionLabels] = useState<string[]>([]);
  const [direccionSearchOpen, setDireccionSearchOpen] = useState(false);
  const [direccionPickerIndex, setDireccionPickerIndex] = useState<number | null>(null);

  const isEdit = mode === 'edit';
  const isClosedPlan = isEdit && String(plan?.estadoPlan || '').trim().toLowerCase() === 'cerrado';

  useEffect(() => {
    if (!open) return;
    setClienteSearchOpen(false);
    setClientePickerIndex(null);
    setDireccionSearchOpen(false);
    setDireccionPickerIndex(null);
    if (isEdit && plan) {
      setFecha(formatDateForInput(plan.fecha));
      setNroVendedor(String(plan.nroVendedor ?? ''));
      setObservaciones(plan.observaciones ?? '');
      setEstadoPlan(plan.estadoPlan ?? '');
      setItems([]);
      setClienteNombres([]);
      setDireccionLabels([]);
      return;
    }

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    setFecha(`${yyyy}-${mm}-${dd}`);
    setNroVendedor('');
    setObservaciones('');
    setEstadoPlan('');
    setItems([]);
    setClienteNombres([]);
    setDireccionLabels([]);
  }, [open, isEdit, plan]);

  const canSubmit = useMemo(() => {
    if (isEdit) return true;
    return Boolean(fecha && nroVendedor);
  }, [isEdit, fecha, nroVendedor]);

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        nroCliente: 0,
        codigoDireccion: '',
        fechaHoraProgramada: null,
        observaciones: '',
        orden: undefined,
      },
    ]);
    setClienteNombres((prev) => [...prev, '']);
    setDireccionLabels((prev) => [...prev, '']);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setClienteNombres((prev) => prev.filter((_, i) => i !== index));
    setDireccionLabels((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, patch: Partial<CreateVisitasPlanItemDto>) => {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        return { ...row, ...patch };
      }),
    );
  };

  const handleSubmit = async () => {
    if (isEdit) {
      await onSubmitEdit({
        observaciones,
        estadoPlan: estadoPlan || undefined,
      });
      return;
    }

    const payload: CreateVisitasPlanDto = {
      fecha,
      nroVendedor: Number(nroVendedor),
      observaciones: observaciones || null,
      items: items
        .filter((i) => Number(i.nroCliente) > 0)
        .map((i) => ({
          nroCliente: Number(i.nroCliente),
          codigoDireccion: i.codigoDireccion?.trim() || undefined,
          fechaHoraProgramada: i.fechaHoraProgramada || null,
          observaciones: i.observaciones || null,
          orden: i.orden,
        })),
    };

    await onSubmitCreate(payload);
  };

  const openClientePicker = (rowIndex: number) => {
    setClientePickerIndex(rowIndex);
    setClienteSearchOpen(true);
  };

  const handleClienteSelected = (cliente: {
    nroCliente: number;
    nombre?: string;
    razonSocial?: string;
  }) => {
    if (clientePickerIndex === null) return;
    const idx = clientePickerIndex;
    updateItem(idx, { nroCliente: cliente.nroCliente, codigoDireccion: '' });
    setDireccionLabels((prev) => {
      const next = [...prev];
      next[idx] = '';
      return next;
    });
    setClienteNombres((prev) => {
      const next = [...prev];
      next[idx] =
        [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') || `Cliente ${cliente.nroCliente}`;
      return next;
    });
    setClienteSearchOpen(false);
    setClientePickerIndex(null);
  };

  const openDireccionPicker = (rowIndex: number) => {
    setDireccionPickerIndex(rowIndex);
    setDireccionSearchOpen(true);
  };

  const handleDireccionSelected = (d: { codigoDireccion: string; direccion: string }) => {
    if (direccionPickerIndex === null) return;
    const idx = direccionPickerIndex;
    updateItem(idx, { codigoDireccion: d.codigoDireccion });
    setDireccionLabels((prev) => {
      const next = [...prev];
      const line = d.direccion || '';
      next[idx] = line.length > 90 ? `${line.slice(0, 87)}…` : line;
      return next;
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{isEdit ? 'Editar Plan de Visitas' : 'Nuevo Plan de Visitas'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Vendedor"
                value={nroVendedor}
                onChange={(e) => setNroVendedor(e.target.value)}
                disabled={isEdit}
              >
                {vendedores.map((v) => (
                  <MenuItem key={v.nroVendedor} value={String(v.nroVendedor)}>
                    {v.nroVendedor} - {v.nombreVendedor || 'Sin nombre'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {isEdit && (
              <Grid item xs={12} md={4}>
                {isClosedPlan ? (
                  <TextField
                    fullWidth
                    label="Estado del Plan"
                    value={estadoPlan || 'Cerrado'}
                    InputProps={{ readOnly: true }}
                    helperText="Plan cerrado: el estado está bloqueado."
                  />
                ) : (
                  <TextField
                    fullWidth
                    select
                    label="Estado del Plan"
                    value={estadoPlan}
                    onChange={(e) => setEstadoPlan(e.target.value)}
                  >
                    <MenuItem value="">Sin cambios</MenuItem>
                    <MenuItem value="Abierto">Abierto</MenuItem>
                    <MenuItem value="EnCurso">EnCurso</MenuItem>
                    <MenuItem value="Cerrado">Cerrado</MenuItem>
                  </TextField>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </Grid>
          </Grid>

          {!isEdit && (
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Visitas iniciales (opcional)
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddItem}>
                  Agregar visita
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {items.map((row, index) => (
                  <Box key={`item-${index}`} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Grid container spacing={1.5} alignItems="flex-start">
                      <Grid item xs={12} md={3}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Cliente (N°)"
                            placeholder="Número o ▼"
                            value={row.nroCliente || ''}
                            onChange={(e) => {
                              updateItem(index, {
                                nroCliente: Number(e.target.value),
                                codigoDireccion: '',
                              });
                              setClienteNombres((prev) => {
                                const next = [...prev];
                                next[index] = '';
                                return next;
                              });
                              setDireccionLabels((prev) => {
                                const next = [...prev];
                                next[index] = '';
                                return next;
                              });
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    color="primary"
                                    title="Buscar cliente"
                                    onClick={() => openClientePicker(index)}
                                  >
                                    <ArrowDropDownIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              minHeight: 36,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.35,
                            }}
                            title={clienteNombres[index] || undefined}
                          >
                            {clienteNombres[index] || 'Escriba el N° o use el selector para buscar.'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Código de dirección"
                            placeholder="Código o ▼"
                            value={row.codigoDireccion || ''}
                            onChange={(e) => {
                              updateItem(index, { codigoDireccion: e.target.value });
                              setDireccionLabels((prev) => {
                                const next = [...prev];
                                next[index] = '';
                                return next;
                              });
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    color="primary"
                                    title="Elegir entre direcciones del cliente"
                                    disabled={!Number(row.nroCliente)}
                                    onClick={() => openDireccionPicker(index)}
                                  >
                                    <ArrowDropDownIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              minHeight: 36,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.35,
                            }}
                            title={direccionLabels[index] || undefined}
                          >
                            {direccionLabels[index] ||
                              (!Number(row.nroCliente)
                                ? 'Indique primero el cliente de la fila.'
                                : 'Escriba el código o abra el listado (▼).')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            label="Programada"
                            InputLabelProps={{ shrink: true }}
                            value={formatDateTimeForInput(row.fechaHoraProgramada)}
                            onChange={(e) => updateItem(index, { fechaHoraProgramada: e.target.value || null })}
                          />
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ mt: 0.5, minHeight: 36, display: 'block', visibility: 'hidden' }}
                            aria-hidden
                          >
                            &nbsp;
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3} md={1}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Orden"
                            value={row.orden ?? ''}
                            onChange={(e) =>
                              updateItem(index, {
                                orden: e.target.value ? Number(e.target.value) : undefined,
                              })
                            }
                          />
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ mt: 0.5, minHeight: 36, display: 'block', visibility: 'hidden' }}
                            aria-hidden
                          >
                            &nbsp;
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={9} md={2}>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Obs. visita"
                            value={row.observaciones || ''}
                            onChange={(e) => updateItem(index, { observaciones: e.target.value })}
                          />
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ mt: 0.5, minHeight: 36, display: 'block', visibility: 'hidden' }}
                            aria-hidden
                          >
                            &nbsp;
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={1}
                        sx={{
                          display: 'flex',
                          justifyContent: { xs: 'flex-start', md: 'center' },
                          pt: { xs: 0, md: 0.5 },
                        }}
                      >
                        <IconButton
                          color="error"
                          size="small"
                          aria-label="Quitar visita"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || saving}>
            {isEdit ? 'Guardar cambios' : 'Crear plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={clienteSearchOpen}
        onClose={() => {
          setClienteSearchOpen(false);
          setClientePickerIndex(null);
        }}
        onSelect={handleClienteSelected}
      />

      <DireccionClienteSearch
        open={direccionSearchOpen}
        onClose={() => {
          setDireccionSearchOpen(false);
          setDireccionPickerIndex(null);
        }}
        nroCliente={
          direccionPickerIndex !== null ? Number(items[direccionPickerIndex]?.nroCliente) || 0 : 0
        }
        onSelect={handleDireccionSelected}
      />
    </>
  );
};

export default PlanFormDialog;
